import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ListHealthChecksVstoreRequest, MonitorApiService } from '@vendasta/wsp-monitor';
import { FeatureFlagService } from './../../../core/feature-flag.service';

@Component({
  selector: 'app-uptime-report',
  templateUrl: './uptime-report.component.html',
  styleUrls: ['./uptime-report.styles.scss'],
  standalone: false,
})
export class UptimeReportComponent implements OnInit {
  @Input() siteId: string;
  @Input() partnerId: string;
  public cursor = '';
  public records: any[] = [];
  public last24Hrs = '0';
  public last7Days = '0';
  public last30Days = '0';
  private showAtlasFeatureFlagId = 'uptime_report';

  constructor(
    private translate: TranslateService,
    public modal: MatDialog,
    private monitorApiService: MonitorApiService,
    private featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    this.featureFlagService.isFeatureEnabled(this.partnerId, this.showAtlasFeatureFlagId, '').subscribe((res) => {
      if (res) {
        this.getSiteReport();
      }
    });
  }

  getSiteReport() {
    const data: ListHealthChecksVstoreRequest = {
      siteId: this.siteId,
      cursor: this.cursor,
      pageSize: 10,
      toApiJson() {
        return {
          siteId: this.siteId,
          cursor: this.cursor,
          pageSize: this.pageSize,
        };
      },
    };
    this.monitorApiService.listHealthChecksVstore(data).subscribe({
      next: (res: any) => {
        if (!this.isEmpty(res) && res.list.length > 0) {
          const array = res.list;
          for (let i = 0; i < array.length; i++) {
            const element = array[i];
            const createdTimestamp = new Date(element.created).getTime();
            const updatedTimestamp = new Date(element.updated).getTime();
            const timeDiffrence = updatedTimestamp - createdTimestamp;
            element.timeDiffrence = timeDiffrence;
            if (element.state != '200') {
              element.time = this.convertTime(timeDiffrence);
            }
            if (i <= 2) {
              element.eventFormatted = this.formatTimestamp(element.created);
            }
            if (i == 0) {
              element.eventFormattedTime = this.formatTime(element.updated);
            }
            this.records.push(element);
            const now = new Date();
            const DaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            const DaysAgoTimestamp = DaysAgo.getTime();
            if (new Date(element.updated).getTime() < DaysAgoTimestamp) {
              break;
            } else {
              this.cursor = element.cursor;
            }
          }
        }
        this.genrateReport();
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  isEmpty(obj) {
    return Object.keys(obj).length === 0;
  }

  genrateReport() {
    this.last24Hrs = this.calculateUptimePercentage(1, this.records);
    this.last7Days = this.calculateUptimePercentage(7, this.records);
    this.last30Days = this.calculateUptimePercentage(30, this.records);
  }

  calculateUptimePercentage(days: number, events) {
    const now = new Date();
    const xDaysAgo = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    const xDaysAgoTimestamp = xDaysAgo.getTime();
    // Filter events for the x days
    const eventsLastXDays = events.filter((event) => new Date(event.created).getTime() >= xDaysAgoTimestamp);

    if (eventsLastXDays.length == 0 && events[0].state != '200') {
      return '0.00';
    } else if (eventsLastXDays.length == 0 && events[0].state == '200') {
      return '100.00';
    }

    // Calculate the total uptime duration in milliseconds
    let totalDownTime = 0;
    eventsLastXDays.forEach((event) => {
      if (event.state != '200') {
        totalDownTime += event.timeDiffrence;
      }
    });

    // Calculate the total time duration in milliseconds for the last 7 days
    const totalTimeDuration = days * 24 * 60 * 60 * 1000;
    const uptime = totalTimeDuration - totalDownTime;
    // Calculate the uptime percentage
    const uptimePercentage = (uptime / totalTimeDuration) * 100;
    return uptimePercentage.toFixed(2); // Return the percentage rounded to 2 decimal places
  }

  convertTime(time) {
    const td = Math.abs(time) / 1000;
    if (td < 60) return Math.floor(td % 60) + ' seconds';
    if (td > 60 && td < 3600) return Math.floor((td % 3600) / 60) + ' minutes';
    if (td > 3600 && td < 86400) return Math.floor((td % 86400) / 3600) + ' hours';
    if (td > 86400 && td < 172800) return Math.floor(td / 86400) + ' day';
    if (td > 172800) return Math.floor(td / 86400) + ' days';
    // return { days, hours, minutes, seconds };
  }

  formatTimestamp(timestamp) {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const dateObj = new Date(timestamp);
    const year = dateObj.getFullYear();
    const month = months[dateObj.getMonth()];
    const day = dateObj.getDate();
    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();
    const seconds = dateObj.getSeconds();
    const meridiem = hours >= 12 ? 'pm' : 'am';
    if (hours > 12) {
      hours -= 12;
    }

    const formattedDate = `${month} ${day}, ${year}, ${hours}:${minutes}:${seconds}${meridiem}`;
    return formattedDate;
  }

  formatTime(timestamp) {
    const dateObj = new Date(timestamp);
    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();
    // const seconds = dateObj.getSeconds();
    const meridiem = hours >= 12 ? 'pm' : 'am';
    if (hours > 12) {
      hours -= 12;
    }

    const minutesString = minutes < 10 ? `0${minutes}` : `${minutes}`;

    const formattedDate = `${hours}:${minutesString}${meridiem}`;
    return formattedDate;
  }
}
